import { useEffect, useRef, useState } from 'react'
import { ColorInput, NumberInput, Select, Slider, Switch, TextInput } from '@mantine/core'
import { IconChevronDown, IconChevronUp, IconSelector, IconViewfinder } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Segmented } from '../v2/Segmented'
import { stablePaletteSuggestions } from '../../utils/aiSuggestions'
import { fieldLabel } from '../../utils/uiLayoutV2'
import classes from './V2Form.module.css'

export const inputClassNames = {
  input: classes.input,
  label: classes.inputLabel,
  error: classes.inputError,
}

const selectClassNames = {
  ...inputClassNames,
  dropdown: classes.dropdown,
  option: classes.option,
}

// The faces a `font-select` previews its options in; the ad itself uses the package's own files.
const FONT_PREVIEW_STYLESHEET = 'https://fonts.googleapis.com/css2?family=Roboto&family=Open+Sans&family=Montserrat&family=Poppins&family=Lato&family=Inter&display=swap'

// An option label names its typeface: "Helvetica/Arial" is the stack Helvetica, Arial.
function fontStack(label) {
  return `${label.split('/').map((name) => `'${name.trim()}'`).join(', ')}, sans-serif`
}

// The swatches a colour-role select offers; blends of two roles stay readable when they are the value.
const SKIN_SWATCHES = /^(c[1-4]|acc|auto|transparent|tint\(c2,100\)|shade\(c2,100\))$/
const COLOUR_TOKEN = /^(c[1-4]|acc|auto|transparent|(mix|shade|tint)\([^)]*\))$/

// Element style selects shown as what each option looks like (AD-252), drawn like the template thumbnails.
const styleIcon = (shape) => (
  <svg width="24" height="24" viewBox="0 0 48 48" fill="none" aria-hidden="true">{shape}</svg>
)
const styleStroke = (d) => styleIcon(
  <path d={d} stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
)
const iconText = (text, y, extra = {}) => (
  <text x="24" y={y} textAnchor="middle" fontFamily="Inter, Helvetica, Arial, sans-serif" fontWeight="700" fontSize="17" fill="currentColor" {...extra}>
    {text}
  </text>
)
const STYLE_ICONS = {
  'data.style.elements.kicker.style': {
    pill: styleIcon(<rect x="5" y="15" width="38" height="18" rx="9" fill="currentColor" />),
    caps: styleIcon(iconText('AB', 31, { letterSpacing: 1 })),
    underline: styleIcon(<>{iconText('Aa', 28)}<rect x="11" y="33" width="26" height="3" rx="1.5" fill="currentColor" /></>),
    ticket: styleIcon(
      <>
        <path d="M7 14h34a2 2 0 0 1 2 2v4.5a3.5 3.5 0 0 0 0 7V32a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-4.5a3.5 3.5 0 0 0 0-7V16a2 2 0 0 1 2-2z" fill="currentColor" />
        <path d="M33 16.5v15" stroke="#fff" strokeWidth="1.5" strokeDasharray="2 2" />
      </>,
    ),
    outline: styleIcon(<rect x="6" y="15.5" width="36" height="17" rx="3" stroke="currentColor" strokeWidth="2.5" fill="none" />),
    sticker: styleIcon(<rect x="5" y="15" width="38" height="18" rx="4" fill="currentColor" transform="translate(24 24) rotate(-8) translate(-24 -24)" />),
  },
  'data.style.elements.countdown.digitStyle': {
    solid: styleIcon(<><rect x="6" y="11" width="36" height="26" rx="5" fill="currentColor" />{iconText('12', 30, { fontSize: 16, fill: '#fff' })}</>),
    outline: styleIcon(
      <>
        <rect x="7.25" y="12.25" width="33.5" height="23.5" rx="4" stroke="currentColor" strokeWidth="2.5" fill="none" />
        {iconText('12', 30, { fontSize: 16 })}
      </>,
    ),
    minimal: styleIcon(iconText('12', 32, { fontSize: 22 })),
    pill: styleIcon(<><rect x="4" y="12" width="40" height="24" rx="12" ry="12" fill="currentColor" />{iconText('12', 30, { fontSize: 16, fill: '#fff' })}</>),
    sticker: styleIcon(
      <g transform="translate(24 24) rotate(-8) translate(-24 -24)">
        <rect x="6" y="11" width="36" height="26" rx="4" fill="currentColor" />
        {iconText('12', 30, { fontSize: 16, fill: '#fff' })}
      </g>,
    ),
  },
  'data.style.elements.bullets.marker': {
    dot: styleIcon(<circle cx="24" cy="24" r="8" fill="currentColor" />),
    check: styleStroke('M13 24.5l7 7 15-15'),
    arrow: styleStroke('M13 24h21M26 15l9 9-9 9'),
    dash: styleStroke('M14 24h20'),
    plus: styleStroke('M24 13v22M13 24h22'),
    star: styleIcon(
      <polygon
        points="24.00,12.00 27.29,20.47 36.36,20.98 29.33,26.73 31.64,35.52 24.00,30.60 16.36,35.52 18.67,26.73 11.64,20.98 20.71,20.47"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />,
    ),
  },
  'data.style.elements.bubble.shape': {
    round: styleIcon(<circle cx="24" cy="24" r="18" fill="currentColor" />),
    burst: styleIcon(
      <polygon
        points="24.00,4.00 27.02,8.80 31.65,5.52 32.61,11.11 38.14,9.86 36.89,15.39 42.48,16.35 39.20,20.98 44.00,24.00 39.20,27.02 42.48,31.65 36.89,32.61 38.14,38.14 32.61,36.89 31.65,42.48 27.02,39.20 24.00,44.00 20.98,39.20 16.35,42.48 15.39,36.89 9.86,38.14 11.11,32.61 5.52,31.65 8.80,27.02 4.00,24.00 8.80,20.98 5.52,16.35 11.11,15.39 9.86,9.86 15.39,11.11 16.35,5.52 20.98,8.80"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />,
    ),
    sticker: styleIcon(<rect x="7" y="7" width="34" height="34" rx="8" fill="currentColor" />),
    tag: styleIcon(
      <path
        d="M15 7h20a6 6 0 0 1 6 6v22a6 6 0 0 1-6 6H15L6.6 25.2a2.5 2.5 0 0 1 0-2.4z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
      />,
    ),
    ribbon: styleIcon(
      <>
        <path d="M2 18h9v16H2l3.5-8z" fill="currentColor" opacity=".55" />
        <path d="M46 18h-9v16h9l-3.5-8z" fill="currentColor" opacity=".55" />
        <path d="M11 34l-4-3h4zM37 34l4-3h-4z" fill="currentColor" opacity=".35" />
        <rect x="7" y="14" width="34" height="17" rx="1.5" fill="currentColor" />
      </>,
    ),
  },
}

function placeholderOf(field) {
  return field?.ui?.placeholder || undefined
}

/** Text / URL input (Figma "Inputs"): label straight on a 36px field. */
export function V2TextField({ field, value, onChange, error, disabled, label, onFocus }) {
  const type = field.type === 'url' ? 'url' : 'text'
  return (
    <TextInput
      label={label ?? fieldLabel(field)}
      placeholder={placeholderOf(field)}
      type={type}
      value={value ?? ''}
      maxLength={field.validation?.maxLength}
      onChange={(event) => onChange(event.currentTarget.value)}
      onFocus={onFocus}
      error={error}
      disabled={disabled}
      classNames={inputClassNames}
    />
  )
}

/** Select with the Figma "selector" icon; option labels come from the schema. */
export function V2SelectField({ field, value, onChange, error, disabled, options, label, onFocus }) {
  const fonts = field?.ui?.control === 'font-select'
  const data = (options || field.options || []).map((option) => ({
    value: String(option.value),
    label: option.label || String(option.value),
  }))
  useEffect(() => {
    if (!fonts || document.querySelector(`link[href="${FONT_PREVIEW_STYLESHEET}"]`)) return
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = FONT_PREVIEW_STYLESHEET
    document.head.appendChild(link)
  }, [fonts])
  return (
    <Select
      label={label ?? fieldLabel(field)}
      placeholder={placeholderOf(field)}
      data={data}
      value={value == null ? null : String(value)}
      allowDeselect={false}
      checkIconPosition="right"
      withCheckIcon={false}
      comboboxProps={{ offset: 4 }}
      rightSection={<IconSelector size={16} stroke={1.6} />}
      rightSectionPointerEvents="none"
      renderOption={fonts ? ({ option }) => <span style={{ fontFamily: fontStack(option.label) }}>{option.label}</span> : undefined}
      onChange={(next) => {
        const original = (options || field.options || []).find((option) => String(option.value) === next)
        onChange(original ? original.value : next)
      }}
      onFocus={onFocus}
      error={error}
      disabled={disabled}
      classNames={selectClassNames}
    />
  )
}

/** Figma "toggle": 14/500 label, 46×24 switch and an On/Off word. */
export function V2Toggle({ field, value, onChange, disabled, label }) {
  const { t } = useTranslation()
  const checked = Boolean(value)
  const text = label ?? fieldLabel(field)
  return (
    <div className={classes.toggle}>
      <span className={classes.toggleLabel}>{text}</span>
      <label className={classes.toggleControl}>
        <Switch
          checked={checked}
          onChange={(event) => onChange(event.currentTarget.checked)}
          disabled={disabled}
          aria-label={text}
          size="md"
          color="#d00b65"
          styles={{
            track: { width: 46, minWidth: 46, height: 24, backgroundColor: checked ? '#d00b65' : '#adb5bd', borderColor: 'transparent', cursor: 'pointer' },
            thumb: { width: 18, height: 18, borderColor: 'transparent' },
          }}
        />
        <span className={classes.toggleState}>{checked ? t('editorV2.form.common.on') : t('editorV2.form.common.off')}</span>
      </label>
    </div>
  )
}

/** Plain number input ("62", "3200 ms"); durations carry the unit as a suffix. */
export function V2NumberField({ field, value, onChange, error, disabled, label }) {
  const rules = field.validation || {}
  const duration = field.type === 'duration'
  return (
    <NumberInput
      label={label ?? fieldLabel(field)}
      value={value == null || value === '' ? '' : Number(value)}
      min={rules.min}
      max={rules.max}
      step={duration ? 100 : (rules.step || 1)}
      suffix={duration ? ' ms' : undefined}
      hideControls
      clampBehavior="blur"
      allowDecimal={false}
      onChange={(next) => {
        if (next === '' || next == null) return
        onChange(Number(next))
      }}
      error={error}
      disabled={disabled}
      classNames={inputClassNames}
    />
  )
}

/** Logo size: the owner-approved slider kept in the Logo panel. */
export function V2SliderField({ field, value, onChange, error, disabled, label }) {
  const rules = field.validation || {}
  const current = Number(value ?? rules.min ?? 0)
  return (
    <div className={classes.stack}>
      <span className={classes.label}>{label ?? fieldLabel(field)}: {current}%</span>
      <Slider
        mt={8}
        aria-label={label ?? fieldLabel(field)}
        min={rules.min}
        max={rules.max}
        step={rules.step || 1}
        value={current}
        onChange={onChange}
        label={(next) => `${next}%`}
        color="#d00b65"
        disabled={disabled}
      />
      {error && <p className={classes.error}>{error}</p>}
    </div>
  )
}

/** A labelled Figma segment control for a small option set. */
export function V2SegmentedField({ field, value, onChange, disabled, options, label, icons = {}, className }) {
  return (
    <div className={classes.stack}>
      <span className={classes.label}>{label ?? fieldLabel(field)}</span>
      <Segmented
        ariaLabel={label ?? fieldLabel(field)}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={className}
        options={(options || field.options || []).map((option) => ({
          value: option.value,
          label: option.label || option.value,
          icon: icons[option.value] || null,
        }))}
      />
    </div>
  )
}

function normalizedHex(value) {
  return String(value || '').trim().toLowerCase()
}

/**
 * Colour input (Figma: 15px dot, hex, "viewfinder" eyedropper) with the "Suggestions" row:
 * the measured brand colours, the ad's palette and house colours, seven at most. The row comes
 * from the form (`palette`, held per ad/run) so it keeps its order when a colour is picked;
 * the current value is marked, not moved.
 */
export function V2ColorField({ field, value, onChange, error, disabled, configuration, label, palette }) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(value || '')
  // What the input last held. Mantine reports a colour as soon as the text parses (so "#abc"
  // while typing "#abc123"); only a complete #rrggbb — typed, picked, a swatch or the
  // eyedropper — is committed, otherwise the half-typed value replaced the text mid-entry.
  const typedRef = useRef(value || '')
  useEffect(() => { setDraft(value || ''); typedRef.current = value || '' }, [value])
  const swatches = (palette?.length ? palette : stablePaletteSuggestions(configuration)).map(normalizedHex)
  const complete = (text) => /^#[0-9a-f]{6}$/i.test(text || '')
  const current = normalizedHex(value)
  const text = label ?? fieldLabel(field)

  return (
    <div className={classes.color}>
      <ColorInput
        label={text}
        value={draft}
        format="hex"
        withPicker
        swatches={swatches}
        swatchesPerRow={7}
        closeOnColorSwatchClick
        eyeDropperIcon={<IconViewfinder size={16} stroke={1.6} />}
        leftSection={<span className={classes.colorDot} style={{ background: value || '#ffffff' }} />}
        onChange={(next) => { typedRef.current = next; setDraft(next) }}
        onChangeEnd={(next) => { if (complete(typedRef.current) && complete(next)) onChange(next) }}
        onBlur={() => {
          if (complete(draft)) onChange(draft)
          else setDraft(value || '')
        }}
        onKeyDown={(event) => { if (event.key === 'Enter' && complete(draft)) onChange(draft) }}
        error={error}
        disabled={disabled}
        classNames={inputClassNames}
        styles={{ colorPreview: { display: 'none' } }}
      />
      <div className={classes.suggestions}>
        <span className={classes.suggestionsLabel}>{t('editorV2.form.common.suggestions')}</span>
        <span className={classes.swatches}>
          {swatches.map((color) => (
            <button
              key={color}
              type="button"
              className={classes.swatch}
              style={{ background: color }}
              data-active={color === current ? 'true' : 'false'}
              aria-label={t('editorV2.form.common.useColor', { color })}
              aria-pressed={color === current}
              title={color}
              disabled={disabled}
              onClick={() => onChange(color)}
            />
          ))}
        </span>
      </div>
    </div>
  )
}

export function isStyleTileField(field) {
  return field?.type === 'select' && Boolean(STYLE_ICONS[field.path])
}

/** An element style as a row of tiles: a grey icon (pink when chosen) with the option's name under it. */
export function V2StyleField({ field, value, onChange, error, disabled, options, label }) {
  const icons = STYLE_ICONS[field.path]
  const text = label ?? fieldLabel(field)
  return (
    <div className={classes.stack}>
      <span className={classes.label}>{text}</span>
      <div className={classes.styleTiles} role="radiogroup" aria-label={text}>
        {(options || field.options || []).map((option) => {
          const active = option.value === value
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              className={classes.styleTile}
              data-active={active ? 'true' : 'false'}
              aria-checked={active}
              disabled={disabled}
              onClick={() => onChange(option.value)}
            >
              <span className={classes.styleIcon}>{icons[option.value]}</span>
              <span className={classes.styleName}>{option.label || option.value}</span>
            </button>
          )
        })}
      </div>
      {error && <p className={classes.error}>{error}</p>}
    </div>
  )
}

/** "Customize Buttons ⌄": a caption on a rule that opens the settings under it. */
export function V2Collapse({ title, defaultOpen = false, bodyClassName = classes.collapseBody, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={classes.full}>
      <div className={classes.collapseHead}>
        <button type="button" className={classes.collapseButton} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {title}
          {open ? <IconChevronUp size={14} stroke={1.8} /> : <IconChevronDown size={14} stroke={1.8} />}
        </button>
        <span className={classes.collapseLine} />
      </div>
      {open && <div className={bodyClassName}>{children}</div>}
    </div>
  )
}

export function isColourTokenField(field) {
  const options = field?.options || []
  return field?.type === 'select' && options.length > 0 && options.every((option) => COLOUR_TOKEN.test(String(option.value)))
}

function mixHex(from, to, amount) {
  const share = Math.min(100, Number(amount)) / 100
  return `#${[1, 3, 5].map((index) => {
    const start = parseInt(from.slice(index, index + 2), 16)
    const end = parseInt(to.slice(index, index + 2), 16)
    return Math.round(start + (end - start) * share).toString(16).padStart(2, '0')
  }).join('')}`
}

// The runtime's colour expressions (split-slider `Runtime.expression`); `auto` and `transparent` have no single colour.
function tokenColour(token, colors) {
  const role = (key) => (/^#[0-9a-f]{6}$/i.test(colors?.[key] || '') ? colors[key] : '#1a1a1a')
  if (/^(c[1-4]|acc)$/.test(token)) return role(token)
  let match = /^(tint|shade)\((c[1-4]|acc),(\d{1,3})\)$/.exec(token)
  if (match) return mixHex(role(match[2]), match[1] === 'tint' ? '#ffffff' : '#000000', match[3])
  match = /^mix\((c[1-4]|acc),(c[1-4]|acc),(\d{1,3})\)$/.exec(token)
  if (match) return mixHex(role(match[1]), role(match[2]), match[3])
  return null
}

/**
 * A colour-role select (button and extra element colours) as one line: the label, then the
 * ad's own colours as swatches with the current one marked (AD-252).
 */
export function V2SkinField({ field, value, onChange, error, disabled, configuration, options, label }) {
  const { t } = useTranslation()
  const shown = (options || field.options || []).filter((option) => SKIN_SWATCHES.test(String(option.value)) || option.value === value)
  const colors = configuration?.data?.colors
  const text = label ?? fieldLabel(field)
  return (
    <div className={classes.stack}>
      <div className={classes.skin}>
        <span className={classes.label}>{text}</span>
        <span className={classes.swatches} role="radiogroup" aria-label={text}>
          {shown.map((option) => {
            const colour = tokenColour(String(option.value), colors)
            const name = option.value === 'auto'
              ? t('editorV2.form.common.autoContrast')
              : `${option.label || option.value}${colour ? ` (${colour.toUpperCase()})` : ''}`
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                className={classes.swatch}
                style={colour ? { background: colour } : undefined}
                data-token={colour ? undefined : option.value}
                data-active={option.value === value ? 'true' : 'false'}
                aria-checked={option.value === value}
                aria-label={name}
                title={name}
                disabled={disabled}
                onClick={() => onChange(option.value)}
              />
            )
          })}
        </span>
      </div>
      {error && <p className={classes.error}>{error}</p>}
    </div>
  )
}
